import { useState, useEffect } from 'react';
import { ChevronRight, Loader2, Zap } from 'lucide-react';
import type { ToolCallDisplay } from '@/lib/types';
import { cn } from '@/lib/utils';

interface ToolCallCardProps {
  tool: ToolCallDisplay;
  /**
   * The owning run has reached a terminal state. A tool with no result can
   * then never complete — the subprocess died mid-call (e.g. SIGKILL) and no
   * tool_result was ever recorded — so it is interrupted, not running.
   */
  runTerminated?: boolean;
}

export function ToolCallCard({ tool, runTerminated }: ToolCallCardProps): React.ReactElement {
  const [expanded, setExpanded] = useState(tool.isExpanded);
  const [showAllOutput, setShowAllOutput] = useState(false);
  const hasResult = tool.output !== undefined || tool.duration !== undefined;
  const isRunning = !hasResult && runTerminated !== true;
  const isInterrupted = !hasResult && runTerminated === true;

  // Live elapsed counter — ticks every second while tool is running
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!isRunning || !tool.startedAt) return;
    setElapsed(Date.now() - tool.startedAt);
    const interval = setInterval(() => {
      setElapsed(Date.now() - tool.startedAt);
    }, 1000);
    return (): void => {
      clearInterval(interval);
    };
  }, [isRunning, tool.startedAt]);

  // Get a brief summary from the input — format as `key="value", key="value"`
  // to match the design's inline tool-call display.
  const inputEntries = Object.entries(tool.input);

  // Limit output display
  const outputLines = tool.output?.split('\n') ?? [];
  const isLongOutput = outputLines.length > 20;
  const displayOutput = showAllOutput ? tool.output : outputLines.slice(0, 20).join('\n');

  return (
    <div
      className={cn(
        'rounded-md border border-border bg-surface-inset transition-colors hover:border-border-bright',
        isRunning && 'border-l-2 border-l-flame'
      )}
    >
      {/* Header - clickable to expand */}
      <button
        onClick={(): void => {
          setExpanded(!expanded);
        }}
        className="flex h-9 w-full items-center gap-2 px-3 text-left"
      >
        <ChevronRight
          className={cn(
            'size-3.5 shrink-0 text-text-tertiary transition-transform duration-150',
            expanded && 'rotate-90'
          )}
        />
        {isRunning ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-flame" />
        ) : (
          <Zap className="size-3.5 shrink-0 text-flame" />
        )}
        <span className="font-mono text-xs font-semibold text-flame-hi">{tool.name}</span>
        {inputEntries.length > 0 && (
          <span className="flex min-w-0 flex-1 items-center gap-0 truncate font-mono text-xs">
            <span className="text-text-tertiary">(</span>
            <span className="min-w-0 truncate text-text-secondary">
              {inputEntries.map(([k, v], i) => {
                const stringVal = typeof v === 'string' ? v : JSON.stringify(v);
                const truncated = stringVal.length > 40 ? stringVal.slice(0, 40) + '…' : stringVal;
                return (
                  <span key={k}>
                    {i > 0 && <span className="text-text-muted">, </span>}
                    <span className="text-text-tertiary">{k}=</span>
                    <span className="text-accent-bright">&quot;{truncated}&quot;</span>
                  </span>
                );
              })}
            </span>
            <span className="text-text-tertiary">)</span>
          </span>
        )}
        <span className="ml-auto shrink-0">
          {isRunning && elapsed > 0 ? (
            <span className="rounded-full border border-flame/30 bg-flame-bg-soft px-2 py-0.5 font-mono text-[10px] text-flame-hi">
              {elapsed < 1000 ? `${String(elapsed)}ms` : `${(elapsed / 1000).toFixed(1)}s`}
            </span>
          ) : tool.duration !== undefined ? (
            <span className="font-mono text-[10px] text-success">
              ✓{' '}
              {tool.duration < 1000
                ? `${String(tool.duration)}ms`
                : `${(tool.duration / 1000).toFixed(1)}s`}
            </span>
          ) : isInterrupted ? (
            <span className="rounded-full border border-border bg-surface-inset px-2 py-0.5 font-mono text-[10px] text-text-tertiary">
              interrupted
            </span>
          ) : null}
        </span>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-border px-3 py-2">
          {Object.keys(tool.input).length > 0 && (
            <div className="mb-2">
              <span className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                Input
              </span>
              <pre className="mt-1 overflow-x-auto rounded-md bg-background p-2 font-mono text-xs text-text-secondary">
                {JSON.stringify(tool.input, null, 2)}
              </pre>
            </div>
          )}
          {tool.output !== undefined && (
            <div>
              <span className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                Output
              </span>
              <pre className="mt-1 max-h-80 overflow-auto rounded-md bg-background p-2 font-mono text-xs text-text-secondary">
                {displayOutput || '(no output)'}
              </pre>
              {isLongOutput && !showAllOutput && (
                <button
                  onClick={(): void => {
                    setShowAllOutput(true);
                  }}
                  className="mt-1 text-xs text-text-secondary hover:text-text-primary"
                >
                  Show {String(outputLines.length - 20)} more lines
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
